import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { secureGet, secureDelete, secureGetPdf } from "../../config/api";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "../../components/PrimaryButton";
import PageScroller from "../../components/PageScroller";
import DeletePopup from "../../components/DeletePopup";
import Filter from "../../components/Filter";
import OptionsMenu from "../../components/OptionsMenu";
import LoadingLogo from "../../components/LoadingLogo";
import ModuleWrapper from "../../components/ModuleWrapper";
import SnackbarNotification from "../../components/SnackbarNotification";
import { saveAs } from "file-saver";

import Card from "../Inventory/Card";
import AddIcon from "../../icons/plusB.svg";
import EditIcon from "../../icons/edit.svg";
import DeleteIcon from "../../icons/trashR.svg";

const Emplacements = () => {
  const { t } = useTranslation();
  const topRef = useRef(null);

  // data
  const [emplacements, setEmplacements] = useState([]);
  const [zones, setZones] = useState([]);
  const [inventories, setInventories] = useState([]);

  // ui
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // filters
  const [filters, setFilters] = useState({ needle: "", inventory: "", zone: "", status: "" });

  // pagination (server-side)
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);

  // modals
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showModule, setShowModule] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const showSnackbar = (message, severity = "success", manual = false) =>
    setSnackbar({ open: true, message, severity, manual });

  const statusOptions = useMemo(
    () => [
      { label: t("inventory_status.NOT_STARTED"), value: "NOT_STARTED" },
      { label: t("inventory_status.IN_PROGRESS"), value: "IN_PROGRESS" },
      { label: t("inventory_status.SUBMITED"), value: "SUBMITED" },
      { label: t("inventory_status.TO_CONTROL"), value: "TO_CONTROL" },
      { label: t("inventory_status.CONTROL_NOT_STARTED"), value: "CONTROL_NOT_STARTED" },
      { label: t("inventory_status.CONTROL_IN_PROGRESS"), value: "CONTROL_IN_PROGRESS" },
      { label: t("inventory_status.CONTROLLED"), value: "CONTROLLED" },
      { label: t("inventory_status.IN_DISPUTE"), value: "IN_DISPUTE" },
      { label: t("inventory_status.VALIDATED"), value: "VALIDATED" }
    ],
    [t]
  );

  const filterOptions = [
    { name: "needle", type: "search" },
    {
      name: "inventory",
      type: "dropdown",
      label: t("fields.inventory.inventaire"),
      options: inventories.map((r) => ({ value: String(r.id), label: r.name })),
    },
    {
      name: "zone",
      type: "dropdown",
      label: t("fields.inventory.zone"),
      options: zones.map((z) => ({ value: String(z.id), label: z.name })),
    },
    { name: "status", label: t("filters.orders.status"), type: "dropdown", options: statusOptions },
  ];

  const pageRows = (data) => (Array.isArray(data) ? data : data?.content ?? []);
  const toInt = (n, fallback) => (Number.isFinite(n) ? n : fallback);

  const buildQuery = (obj) => {
    const p = new URLSearchParams();
    Object.entries(obj).forEach(([k, v]) => {
      if (v !== undefined && v !== null && String(v).trim() !== "") p.set(k, String(v).trim());
    });
    return p.toString();
  };

  const fetchZones = useCallback(
    async (inventoryId = "") => {
      try {
        const qs = buildQuery({ inventoryId, size: 1000});
        const url = qs ? `/inventory-zones?${qs}` : "/inventory-zones?size=1000";
        const z = await secureGet(url, showSnackbar);
        setZones(pageRows(z?.data));
      } catch (error) {
        console.error("Error fetching zones:", error);
      }
    },
    []
  );

  const fetchPaginatedEmplacements = useCallback(
    async (page = pageNumber, size = pageSize, active = filters) => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.set("page", String(page));
        params.set("size", String(size));

        const n = (active.needle || "").trim();
        if (n) params.set("needle", n);

        const inv = (active.inventory || "").trim();
        if (inv) params.set("inventoryId", inv);

        const zn = (active.zone || "").trim();
        if (zn) params.set("zoneId", zn);

        const st = (active.status || "").trim();
        if (st) params.set("status", st);

        const res = await secureGet(`/inventory-locations?${params.toString()}`, showSnackbar, { signal });
        const pageData = res?.data ?? {};
        const content = pageRows(pageData);

        setEmplacements(content);
        setTotalPages(toInt(pageData.totalPages, 1));
        if (Number.isFinite(pageData.number)) setPageNumber(pageData.number);
        if (Number.isFinite(pageData.size)) setPageSize(pageData.size);
      } catch (error) {
        if (error.name !== "AbortError") console.error("Error fetching emplacements:", error);
      } finally {
        setLoading(false);
      }
    },
    [filters, pageNumber, pageSize, signal]
  );

  useEffect(() => {
    (async () => {
      try {
        await fetchZones("");
        const r = await secureGet("/inventories?size=1000", showSnackbar, { signal });
        setInventories(pageRows(r?.data));
        await fetchPaginatedEmplacements(0, pageSize, { needle: "", inventory: "", zone: "", status: "" });
      } catch (e) {
        if (e.name !== "AbortError") console.error(e);
      }
    })();
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (name, value) => {
    if (name === "inventory") {
      const updated = { ...filters, inventory: value, zone: "" };
      setFilters(updated);
      setPageNumber(0);
      fetchZones(value);
      fetchPaginatedEmplacements(0, pageSize, updated);
      return;
    }
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    setPageNumber(0);
    fetchPaginatedEmplacements(0, pageSize, updated);
  };

  const resetFilters = () => {
    const reset = { needle: "", inventory: "", zone: "", status: "" };
    setFilters(reset);
    setPageNumber(0);
    fetchZones("");
    fetchPaginatedEmplacements(0, pageSize, reset);
  };

  const handlePageChange = (p) => {
    setPageNumber(p);
    fetchPaginatedEmplacements(p, pageSize, filters);
    topRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (n) => {
    const sizeNum = Number(n) || pageSize;
    setPageSize(sizeNum);
    setPageNumber(0);
    fetchPaginatedEmplacements(0, sizeNum, filters);
  };

  const handleAddClick = () => {
    setSelectedItem(null);
    setShowModule(true);
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setShowModule(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      await secureDelete(`/inventory-locations/${itemToDelete.id}`, showSnackbar);
      await fetchPaginatedEmplacements(pageNumber, pageSize, filters);
      showSnackbar(t("confirmation.inventory-locationsDelete"), "success");
    } catch (error) {
      console.error("Error deleting emplacement:", error);
    } finally {
      setDeletePopupVisible(false);
    }
  };

  const handleModuleClose = async (action = "cancel") => {
    if (action === "submit") await fetchPaginatedEmplacements(pageNumber, pageSize, filters);
    setShowModule(false);
    setSelectedItem(null);
  };

  const getMenuOptions = (item) => [
    { label: t("actions.modify"), icon: EditIcon, action: () => handleEditClick(item) },
    { label: t("actions.delete"), icon: DeleteIcon, color: "#EF476F", action: () => handleDeleteClick(item) },
  ];

  const handleDownload = async () => {
    const inventoryId = (filters?.inventory || "").trim();
    if (!inventoryId) {
      showSnackbar(t("errors.selectInventoryFirst"), "error");
      return;
    }
    try {
      const response = await secureGetPdf(`/inventories/labels.pdf?inventoryId=${inventoryId}`, showSnackbar);
      const today = new Date().toISOString().split("T")[0];
      saveAs(new Blob([response.data], { type: "application/pdf" }), `codes_barres_${inventoryId}_${today}.pdf`);
      showSnackbar(t("confirmation.downloadSuccess") || "Téléchargement réussi.", "success");
    } catch (error) {
      console.error("Failed to download labels:", error);
    }
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.inventory_emplacements")}</title>
      </Helmet>

      {/* Header */}
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t("titles.inventory_emplacements")}</h1>
        <div className="ml-auto text-[16px] flex gap-3">
          <PrimaryButton label={t(`primaryButtons.download_codebar`)} icon={AddIcon} onClick={handleDownload} />
          <PrimaryButton label={t(`primaryButtons.add_emplacement`)} icon={AddIcon} onClick={handleAddClick} />
        </div>
      </div>

      {/* Filters */}
      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      {/* Content */}
      <div style={{ position: "relative", minHeight: "200px" }}>
        {loading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        )}

        <div className="mt-6 grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {!loading && emplacements.length > 0 ? (
            emplacements.map((e) => {
              const title = e.barcode;
              const name = `${e.zoneName} - ${e.name}`;
              return (
                <Card
                  key={e.id}
                  label={title}
                  title={name}
                  status={e.status}
                  optionsMenu={
                    <div onClick={(ev) => ev.stopPropagation()}>
                      <OptionsMenu options={getMenuOptions(e)} />
                    </div>
                  }
                />
              );
            })
          ) : (
            !loading && (
              <div className="text-center mt-4 col-span-full">
              </div>
            )
          )}
        </div>
      </div>

      {/* Pagination */}
      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Delete */}
      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Module */}
      {showModule && (
        <ModuleWrapper
          moduleType="inventory-locations"
          mode={selectedItem ? "modify" : "add"}
          onClose={handleModuleClose}
          onSuccess={(msg) => showSnackbar(msg, "success")}
          initialValues={selectedItem || {}}
        />
      )}

      {/* Snackbar */}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default Emplacements;
